import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { PageLoader } from '../components/common';
import { Plus, Trash2, Save, X, Eye, EyeOff, ChevronDown, Search, TreePine, Settings, List, Square, AlertTriangle, Download, Send, Zap, Star, Sparkles } from 'lucide-react';
import { tenantApi, PrimerQuestion, CTA, PrimerQuestionConfig, QuestionLabel, QuestionOpsPlanParams } from '../lib/tenant-api';
import {
  buildAllQuestionsSDLPayload,
  buildOptimizableSDLPayload,
  downloadSDLPayloadAsJson,
  DISTRIBUTABLE_SDL_FILENAME,
  resolveSDLBrandName,
} from '../lib/sdl-export';
import { useTenantAuth } from '../contexts/TenantAuthContext';
import AssetSelectorModal from '../components/AssetSelectorModal';
import { RichTextEditor, RichTextEditorRef } from '../components/rich-text-editor';
import {
  QuestionItem,
  SortableQuestionItem,
  CTASelector,
  FollowUpSelector,
  ParentRemovalSelector,
  TreeViewModal,
  getCTATypeColor as getCTATypeColorUtil
} from '../components/primer-questions';
import QuestionPerformanceModal from '../components/primer-questions/QuestionPerformanceModal';
import QuestionAIAssistantDrawer from '../components/primer-questions/QuestionAIAssistantDrawer';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

interface PrimerQuestionForm {
  question: string;
  answer: string;
  parent_ids: string[];
  is_active: boolean;
  is_root: boolean;
  is_conversion: boolean;
  is_optimizable: boolean;
  is_first_class: boolean;
  order_index: number;
  asset_selection_mode: 'script' | 'tags';
  asset_tags: string[];
  is_faq: boolean;
  question_type: 'primer' | 'faq';
  answer_summary: string | null;
}

/** Matches full phrase in question/answer, or requires all tokens present when the query has multiple tokens. */
function primerQuestionMatchesSearch(question: string, answer: string, searchTerm: string): boolean {
  const needle = searchTerm.trim().toLowerCase();
  if (!needle) return true;
  const q = question.toLowerCase();
  const a = answer.toLowerCase();
  const inText = (s: string) => q.includes(s) || a.includes(s);
  if (inText(needle)) return true;
  const words = needle.split(/\s+/).filter(Boolean);
  return words.length > 1 && words.every(w => inText(w));
}

export default function PrimerQuestionsManagement() {
  const { tenant } = useTenantAuth();
  const [questions, setQuestions] = useState<PrimerQuestion[]>([]);
  const [ctas, setCtas] = useState<CTA[]>([]);
  const [questionCTAs, setQuestionCTAs] = useState<Record<string, CTA[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<PrimerQuestionConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showFollowUpSelector, setShowFollowUpSelector] = useState(false);
  const [showCTASelector, setShowCTASelector] = useState(false);
  const [showParentRemovalSelector, setShowParentRemovalSelector] = useState(false);
  const [showTreeView, setShowTreeView] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedParentForFollowUp, setSelectedParentForFollowUp] = useState<PrimerQuestion | null>(null);
  const [selectedQuestionForCTA, setSelectedQuestionForCTA] = useState<PrimerQuestion | null>(null);
  const [selectedQuestionForParentRemoval, setSelectedQuestionForParentRemoval] = useState<PrimerQuestion | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<PrimerQuestion | null>(null);
  const [editSource, setEditSource] = useState<'tree-view' | 'default'>('default');
  const [showAssetSelector, setShowAssetSelector] = useState(false);
  const editorRef = useRef<RichTextEditorRef>(null);
  const [cacheMessage, setCacheMessage] = useState<string>('');
  const [cacheMessageType, setCacheMessageType] = useState<'success' | 'error' | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // View & selection state
  const [currentView, setCurrentView] = useState<'root' | 'all' | 'faq' | 'other'>('root');
  const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(new Set());
  const [showBulkDeleteWarning, setShowBulkDeleteWarning] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [questionsToDelete, setQuestionsToDelete] = useState<PrimerQuestion[]>([]);
  const [questionsWithParents, setQuestionsWithParents] = useState<PrimerQuestion[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFAQPreview, setShowFAQPreview] = useState(false);
  const [faqKey, setFaqKey] = useState(0);

  // Filtering state
  const [selectedLabel, setSelectedLabel] = useState<string>('');
  const [pendingLabel, setPendingLabel] = useState<string>('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [pendingTypes, setPendingTypes] = useState<string[]>([]);
  const [showLabelDropdown, setShowLabelDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<'' | 'active' | 'inactive'>('');
  const [pendingStatus, setPendingStatus] = useState<'' | 'active' | 'inactive'>('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [selectedLarsFilter, setSelectedLarsFilter] = useState(false);
  const [pendingLarsFilter, setPendingLarsFilter] = useState(false);
  const [selectedOptimizableFilter, setSelectedOptimizableFilter] = useState(false);
  const [pendingOptimizableFilter, setPendingOptimizableFilter] = useState(false);
  const [selectedContextType, setSelectedContextType] = useState<string>('');
  const [pendingContextType, setPendingContextType] = useState<string>('');
  const [showContextTypeDropdown, setShowContextTypeDropdown] = useState(false);

  const labelDropdownRef = useRef<HTMLDivElement>(null);
  const typeDropdownRef = useRef<HTMLDivElement>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const contextTypeDropdownRef = useRef<HTMLDivElement>(null);
  const [showTabDownloadDropdown, setShowTabDownloadDropdown] = useState(false);
  const tabDownloadDropdownRef = useRef<HTMLDivElement>(null);

  // Card expansion & follow-up display state
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [displayFollowUps, setDisplayFollowUps] = useState<Set<string>>(new Set());

  // Per-question performance view (score trend + answer history)
  const [performanceQuestion, setPerformanceQuestion] = useState<PrimerQuestion | null>(null);

  // Labels
  const [availableLabels, setAvailableLabels] = useState<QuestionLabel[]>([]);

  // LAWMA Labs SDL state
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const downloadDropdownRef = useRef<HTMLDivElement>(null);
  const [showBulkAssignDropdown, setShowBulkAssignDropdown] = useState(false);
  const [showAIOps, setShowAIOps] = useState(false);
  const bulkAssignDropdownRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState<PrimerQuestionForm>({
    question: '',
    answer: '',
    parent_ids: [],
    is_active: true,
    is_root: false,
    is_conversion: false,
    is_optimizable: true,
    is_first_class: false,
    order_index: 0,
    asset_selection_mode: 'script',
    asset_tags: [],
    is_faq: false,
    question_type: 'primer',
    answer_summary: null
  });

  // ── Data fetching ──────────────────────────────────────────────────────

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      // getPrimerQuestions already excludes retired questions (internal history).
      const data = await tenantApi.getPrimerQuestions();
      setQuestions(data);
    } catch (err) {
      setError('Failed to fetch primer questions');
      console.error('Error fetching primer questions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCTAs = async () => {
    try {
      const data = await tenantApi.getCTAs();
      setCtas(data);
    } catch (err) {
      console.error('Error fetching CTAs:', err);
    }
  };

  // Silent refresh after the AI applies changes — updates questions, labels and
  // CTAs in place WITHOUT toggling the full-page loader, so new/edited/deleted
  // questions just appear.
  const refreshAfterAIChanges = async () => {
    try {
      const data = await tenantApi.getPrimerQuestions();
      setQuestions(data);
      await Promise.all([fetchLabels(), loadAllQuestionCTAsBatch()]);
    } catch (err) {
      console.error('Silent refresh after AI changes failed:', err);
    }
  };

  const fetchQuestionCTAs = async (questionId: string) => {
    try {
      const data = await tenantApi.getPrimerQuestionCTAs(questionId);
      setQuestionCTAs(prev => ({ ...prev, [questionId]: data }));
    } catch (err) {
      console.error('Error fetching question CTAs:', err);
    }
  };

  const loadAllQuestionCTAsBatch = async () => {
    try {
      const data = await tenantApi.getAllPrimerQuestionCTAs();
      setQuestionCTAs(data);
    } catch (err) {
      console.error('Error fetching batch question CTAs:', err);
    }
  };

  const fetchLabels = async () => {
    try {
      const data = await tenantApi.getQuestionLabels();
      setAvailableLabels(data);
    } catch (err) {
      console.error('Error fetching question labels:', err);
    }
  };

  const fetchConfig = async () => {
    try {
      setConfigLoading(true);
      const data = await tenantApi.getPrimerQuestionConfig();
      setConfig(data);
    } catch (err) {
      setConfigError('Failed to fetch primer question configuration');
      console.error('Error fetching primer question config:', err);
    } finally {
      setConfigLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchQuestions(), fetchCTAs(), loadAllQuestionCTAsBatch(), fetchConfig(), fetchLabels()]);
    };
    loadData();
  }, []);

  // Click-outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (labelDropdownRef.current && !labelDropdownRef.current.contains(event.target as Node)) setShowLabelDropdown(false);
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target as Node)) setShowTypeDropdown(false);
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) setShowStatusDropdown(false);
      if (contextTypeDropdownRef.current && !contextTypeDropdownRef.current.contains(event.target as Node)) setShowContextTypeDropdown(false);
      if (downloadDropdownRef.current && !downloadDropdownRef.current.contains(event.target as Node)) setShowDownloadDropdown(false);
      if (tabDownloadDropdownRef.current && !tabDownloadDropdownRef.current.contains(event.target as Node)) setShowTabDownloadDropdown(false);
      if (bulkAssignDropdownRef.current && !bulkAssignDropdownRef.current.contains(event.target as Node)) setShowBulkAssignDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Memoized derived data ─────────────────────────────────────────────

  // Reload FAQ widget when selectedLabel changes (force remount so widget picks up new data-label)
  useEffect(() => {
    if (showFAQPreview) {
      clearFAQLocalStorage();
      setFaqKey(prev => prev + 1);
    }
  }, [selectedLabel]);

  const conversionQuestions = useMemo(() => questions.filter(q => q.is_conversion), [questions]);
  const faqQuestions = useMemo(() => questions.filter(q => q.is_faq), [questions]);
  const otherQuestions = useMemo(() => questions.filter(q => !q.is_conversion && !q.is_faq), [questions]);
  const availableContextTypes = useMemo(() => {
    const contextTypeSet = new Set(
      questions
        .map(q => q.context_type?.trim())
        .filter((value): value is string => Boolean(value))
    );
    return Array.from(contextTypeSet).sort((a, b) => a.localeCompare(b));
  }, [questions]);

  const getFollowUpQuestions = useCallback((parentId: string): PrimerQuestion[] => {
    return questions.filter(q => q.parent_ids && q.parent_ids.includes(parentId));
  }, [questions]);

  /** Shared filter: applies search, label and type filters to any question array */
  const applyFilters = useCallback((qs: PrimerQuestion[]) => {
    let filtered = qs;
    if (searchTerm) filtered = filtered.filter(q => primerQuestionMatchesSearch(q.question, q.answer, searchTerm));
    if (selectedLabel && selectedLabel !== 'all') filtered = filtered.filter(q => q.labels && q.labels.some(l => l.name === selectedLabel));
    if (selectedTypes.length > 0) filtered = filtered.filter(q => selectedTypes.some(type => (type === 'root' && q.is_root) || (type === 'faq' && q.is_faq) || (type === 'conversion' && q.is_conversion) || (type === 'first_class' && q.is_first_class)));
    if (selectedStatus === 'active') filtered = filtered.filter(q => q.is_active);
    else if (selectedStatus === 'inactive') filtered = filtered.filter(q => !q.is_active);
    if (selectedContextType) filtered = filtered.filter(q => (q.context_type || '') === selectedContextType);
    if (selectedLarsFilter) filtered = filtered.filter(q => q.is_lars === true);
    if (selectedOptimizableFilter) filtered = filtered.filter(q => q.is_optimizable === true);
    return filtered;
  }, [searchTerm, selectedLabel, selectedTypes, selectedStatus, selectedContextType, selectedLarsFilter, selectedOptimizableFilter]);

  const filteredQuestions = useMemo(() =>
    applyFilters(questions).sort((a, b) => a.order_index - b.order_index || a.id.localeCompare(b.id)),
    [questions, applyFilters]
  );

  // Grounding hints for the AI editor: the list's active filter as a plain shape.
  const aiFilters = useMemo<QuestionOpsPlanParams['filters']>(() => {
    const f: NonNullable<QuestionOpsPlanParams['filters']> = {};
    if (selectedContextType) f.context_type = selectedContextType;
    if (selectedLarsFilter) f.is_lars = true;
    if (selectedOptimizableFilter) f.is_optimizable = true;
    if (currentView === 'faq' || selectedTypes.includes('faq')) f.is_faq = true;
    if (selectedTypes.includes('first_class')) f.is_first_class = true;
    return Object.keys(f).length ? f : undefined;
  }, [selectedContextType, selectedLarsFilter, selectedOptimizableFilter, currentView, selectedTypes]);

  const filteredConversionQuestions = useMemo(() =>
    applyFilters(conversionQuestions),
    [conversionQuestions, applyFilters]
  );

  const filteredFaqQuestions = useMemo(() =>
    applyFilters(faqQuestions),
    [faqQuestions, applyFilters]
  );

  const filteredOtherQuestions = useMemo(() =>
    applyFilters(otherQuestions),
    [otherQuestions, applyFilters]
  );

  // Every tab's questions in display order (keyed by tab) — so the AI can resolve
  // positional references to the exact row the admin sees, in ANY tab they name
  // ("question 5 in the all tab"), not just the one currently open.
  const tabOrders = useMemo(() => ({
    all: filteredQuestions.map((q) => q.id),
    conversion: filteredConversionQuestions.map((q) => q.id),
    faq: filteredFaqQuestions.map((q) => q.id),
    unassigned: filteredOtherQuestions.map((q) => q.id),
  }), [filteredQuestions, filteredConversionQuestions, filteredFaqQuestions, filteredOtherQuestions]);

  const activeTab =
    currentView === 'root' ? 'conversion' :
    currentView === 'faq' ? 'faq' :
    currentView === 'other' ? 'unassigned' :
    'all';

  // ── Handlers ───────────────────────────────────────────────────────────

  const handleCreateLabel = async (name: string): Promise<QuestionLabel | null> => {
    if (!name.trim()) return null;
    try {
      const newLabel = await tenantApi.createQuestionLabel(name.trim());
      await fetchLabels();
      return newLabel;
    } catch (err: any) {
      if (err.response?.status === 400) setError('A label with this name already exists');
      else setError('Failed to create label');
      console.error('Error creating label:', err);
      return null;
    }
  };

  const handleAssignLabels = async (questionId: string, labelIds: string[]) => {
    try {
      await tenantApi.assignLabelsToQuestion(questionId, labelIds);
      await fetchQuestions();
    } catch (err) {
      setError('Failed to assign labels');
      console.error('Error assigning labels:', err);
    }
  };

  const handleConfigChange = async (revertToRootOnDeadend: boolean) => {
    try {
      setConfigLoading(true);
      setConfigError(null);
      const updatedConfig = await tenantApi.updatePrimerQuestionConfig({ revert_to_root_on_deadend: revertToRootOnDeadend });
      setConfig(updatedConfig);
    } catch (err) {
      setConfigError('Failed to update configuration');
      console.error('Error updating primer question config:', err);
    } finally {
      setConfigLoading(false);
    }
  };

  const isOrphanedQuestion = useCallback((question: PrimerQuestion) => !question.parent_ids || question.parent_ids.length === 0, []);

  const handleSelectAll = useCallback(() => {
    if (selectedQuestions.size === filteredQuestions.length) setSelectedQuestions(new Set());
    else setSelectedQuestions(new Set(filteredQuestions.map(q => q.id)));
  }, [selectedQuestions.size, filteredQuestions]);

  const handleSelectQuestion = useCallback((questionId: string) => {
    setSelectedQuestions(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(questionId)) newSelected.delete(questionId);
      else newSelected.add(questionId);
      return newSelected;
    });
  }, []);

  const handleBulkDelete = useCallback(() => {
    if (selectedQuestions.size === 0) return;
    const selectedQuestionsArray = questions.filter(q => selectedQuestions.has(q.id));
    const orphaned = selectedQuestionsArray.filter(isOrphanedQuestion);
    const withParents = selectedQuestionsArray.filter(q => !isOrphanedQuestion(q));
    if (withParents.length > 0) { setQuestionsWithParents(withParents); setQuestionsToDelete(orphaned); setShowBulkDeleteWarning(true); }
    else { setQuestionsToDelete(orphaned); setShowBulkDeleteConfirm(true); }
  }, [selectedQuestions, questions, isOrphanedQuestion]);

  const handleConfirmBulkDelete = useCallback(async () => {
    try {
      for (const question of questionsToDelete) await tenantApi.deletePrimerQuestion(question.id);
      await fetchQuestions();
      setSelectedQuestions(new Set());
      setQuestionsToDelete([]);
      setShowBulkDeleteConfirm(false);
      setShowBulkDeleteWarning(false);
    } catch (err) {
      setError('Failed to delete selected questions');
      console.error('Error deleting questions:', err);
    }
  }, [questionsToDelete]);

  const handleBulkEnableLars = useCallback(async () => {
    if (selectedQuestions.size === 0) return;
    try {
      for (const id of selectedQuestions) await tenantApi.updatePrimerQuestion(id, { is_optimizable: true, is_lars: true });
      await fetchQuestions();
      setSelectedQuestions(new Set());
    } catch (err) {
      setError('Failed to enable LARS for selected questions');
      console.error('Error enabling LARS for questions:', err);
    }
  }, [selectedQuestions]);

  const handleBulkMakeFirstClass = useCallback(async () => {
    if (selectedQuestions.size === 0) return;
    try {
      for (const id of selectedQuestions) await tenantApi.updatePrimerQuestion(id, { is_first_class: true });
      await fetchQuestions();
      setSelectedQuestions(new Set());
    } catch (err) {
      setError('Failed to mark selected questions as first class');
      console.error('Error marking questions as first class:', err);
    }
  }, [selectedQuestions]);

  const handleCancelBulkDelete = useCallback(() => {
    setQuestionsToDelete([]);
    setQuestionsWithParents([]);
    setShowBulkDeleteConfirm(false);
    setShowBulkDeleteWarning(false);
  }, []);

  const clearFAQLocalStorage = () => { try { localStorage.removeItem('faq-state'); } catch (error) { console.error('Error clearing FAQ localStorage:', error); } };

  const reloadFAQWidget = () => {
    clearFAQLocalStorage();
    if (showFAQPreview) {
      const faqContainer = document.getElementById('display-lawmafaqai');
      if (faqContainer) {
        faqContainer.innerHTML = '';
        window.dispatchEvent(new CustomEvent('faq-widget-reload'));
        setTimeout(() => { if (faqContainer.children.length === 0) console.log('FAQ widget should auto-reinitialize'); }, 100);
      }
    }
  };

  const handleClearCache = async () => {
    try {
      setCacheMessage(''); setCacheMessageType('');
      const response = await tenantApi.clearPromptCache();
      clearFAQLocalStorage();
      setCacheMessage(response.message); setCacheMessageType('success');
      setTimeout(() => { setCacheMessage(''); setCacheMessageType(''); }, 5000);
    } catch (error) {
      console.error('Error clearing cache:', error);
      setCacheMessage('Failed to clear cache. Please try again.'); setCacheMessageType('error');
      setTimeout(() => { setCacheMessage(''); setCacheMessageType(''); }, 5000);
    }
  };

  const handleEdit = (question: PrimerQuestion, source: 'tree-view' | 'default' = 'default') => {
    setEditingQuestion(question);
    setEditSource(source);
    setFormData({
      question: question.question, answer: question.answer, parent_ids: question.parent_ids || [],
      is_active: question.is_active, is_root: question.is_root, is_conversion: question.is_conversion || false,
      is_optimizable: question.is_optimizable || false, is_first_class: question.is_first_class || false,
      order_index: question.order_index, asset_selection_mode: question.asset_selection_mode || 'script',
      asset_tags: question.asset_tags || [], is_faq: question.is_faq || false,
      question_type: question.question_type || 'primer', answer_summary: question.answer_summary || null
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this primer question? This will also remove it as a follow-up from any parent questions.')) return;
    try {
      // Performance-tracked questions are retained server-side ({retired: true})
      // but disappear from the tenant's view either way — same outcome for them.
      await tenantApi.deletePrimerQuestion(id);
      toast.success('Question deleted');
      clearFAQLocalStorage();
      await fetchQuestions();
    } catch (err) {
      setError('Failed to delete primer question');
      console.error('Error deleting primer question:', err);
    }
  };

  const handleToggleActive = async (question: PrimerQuestion) => {
    try {
      await tenantApi.updatePrimerQuestion(question.id, { is_active: !question.is_active });
      await handleClearCache();
      await fetchQuestions();
    } catch (err) {
      setError('Failed to update primer question status');
      console.error('Error updating primer question status:', err);
    }
  };

  const handleAddFollowUp = useCallback((parentQuestion: PrimerQuestion) => {
    setSelectedParentForFollowUp(parentQuestion);
    setShowFollowUpSelector(true);
  }, []);

  const handleFollowUpSelection = async (questionIds: string[]) => {
    if (!selectedParentForFollowUp) return;
    try {
      for (const questionId of questionIds) await tenantApi.addParentToQuestion(questionId, selectedParentForFollowUp.id);
      await fetchQuestions();
      setSelectedParentForFollowUp(null);
    } catch (err) {
      setError('Failed to add follow-up questions');
      console.error('Error adding follow-up questions:', err);
    }
  };

  const handleParentRemoval = async (parentIds: string[]) => {
    if (!selectedQuestionForParentRemoval) return;
    try {
      for (const parentId of parentIds) await tenantApi.removeParentFromQuestion(selectedQuestionForParentRemoval.id, parentId);
      await fetchQuestions();
      setSelectedQuestionForParentRemoval(null);
    } catch (err) {
      setError('Failed to remove parent relationships');
      console.error('Error removing parent relationships:', err);
    }
  };

  const handleManageCTAs = (question: PrimerQuestion) => {
    setSelectedQuestionForCTA(question);
    setShowCTASelector(true);
  };

  const handleCTASelection = async (ctaIds: string[]) => {
    if (!selectedQuestionForCTA) return;
    try {
      for (const ctaId of ctaIds) await tenantApi.assignCTAToPrimerQuestion(selectedQuestionForCTA.id, ctaId);
      await fetchQuestionCTAs(selectedQuestionForCTA.id);
      setSelectedQuestionForCTA(null);
    } catch (err) {
      setError('Failed to assign CTAs to question');
      console.error('Error assigning CTAs to question:', err);
    }
  };

  const handleRemoveCTA = async (questionId: string, ctaId: string) => {
    try {
      await tenantApi.removeCTAFromPrimerQuestion(questionId, ctaId);
      await fetchQuestionCTAs(questionId);
    } catch (err) {
      setError('Failed to remove CTA from question');
      console.error('Error removing CTA from question:', err);
    }
  };

  const getCTATypeColor = (type: string) => getCTATypeColorUtil(type);

  const handleAssetsSelected = (data: { mode: 'script' | 'tags' | 'carousel'; markdownStrings?: string[]; tags?: string[]; carouselMarkup?: string }) => {
    if (data.mode === 'script' && data.markdownStrings) {
      const markdownText = data.markdownStrings.join('\n');
      editorRef.current?.insertRawText('\n' + markdownText);
    } else if (data.mode === 'carousel' && data.carouselMarkup) {
      editorRef.current?.insertRawText('\n' + data.carouselMarkup);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.question.trim()) { setError('Question is required'); return; }
    if (!formData.answer.trim()) { setError('Answer is required'); return; }
    setIsSubmitting(true);
    try {
      const wasFaq = editingQuestion?.is_faq || formData.is_faq;
      if (editingQuestion) {
        // Note: a text change on a performance-tracked question makes the API
        // return a replacement question (new id) and hide the original. To the
        // tenant this is just an update — the refetch swaps the rows in place,
        // so the response id is intentionally not relied upon here.
        await tenantApi.updatePrimerQuestion(editingQuestion.id, formData);
        toast.success('Question updated');
        await handleClearCache();
        await fetchQuestions();
      } else {
        // Create with order_index 0 (top), shift existing questions down
        const created = await tenantApi.createPrimerQuestion({ ...formData, order_index: 0 });
        await handleClearCache();
        // Fetch fresh list (already excludes retired rows), put the new question
        // first, reindex all. Only order_index is sent — resending text could
        // otherwise interact with server-side versioning.
        const fresh = await tenantApi.getPrimerQuestions();
        const newQ = fresh.find(q => q.id === created?.id);
        const rest = fresh.filter(q => q.id !== created?.id).sort((a, b) => a.order_index - b.order_index || a.id.localeCompare(b.id));
        const reordered = newQ ? [newQ, ...rest] : fresh;
        await Promise.all(reordered.map((q, i) =>
          tenantApi.updatePrimerQuestion(q.id, { order_index: i })
        ));
        setQuestions(reordered.map((q, i) => ({ ...q, order_index: i })));
        setLoading(false);
      }
      if (wasFaq) reloadFAQWidget();
      resetForm();
    } catch (err: any) {
      let errorMessage = 'Failed to save primer question';
      if (err.response?.status === 422) {
        if (err.response.data?.detail) {
          if (Array.isArray(err.response.data.detail)) errorMessage = `Validation error: ${err.response.data.detail.map((e: any) => `${e.loc?.join('.')} - ${e.msg}`).join(', ')}`;
          else errorMessage = `Validation error: ${err.response.data.detail}`;
        }
      } else if (err.response?.status === 409) {
        // The question was replaced/removed server-side since this list loaded.
        errorMessage = 'This question can no longer be edited. The list has been refreshed — please try again.';
        resetForm();
        await fetchQuestions();
      } else if (err.response?.status === 401) errorMessage = 'Authentication failed. Please log in again.';
      else if (err.response?.status === 403) errorMessage = 'Permission denied.';
      else if (err.response?.data?.detail) errorMessage = `Failed to save: ${err.response.data.detail}`;
      else if (err.response?.data?.message) errorMessage = `Failed to save: ${err.response.data.message}`;
      else if (err.message) errorMessage = `Failed to save: ${err.message}`;
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ question: '', answer: '', parent_ids: [], is_active: true, is_root: false, is_conversion: false, is_optimizable: true, is_first_class: false, order_index: 0, asset_selection_mode: 'script', asset_tags: [], is_faq: false, question_type: 'primer', answer_summary: null });
    setEditingQuestion(null);
    setShowForm(false);
  };

  // ── Drag-and-drop (All list) ──────────────────────────────────────────

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Build the current sorted order (global)
    const sorted = [...questions].sort((a, b) => a.order_index - b.order_index || a.id.localeCompare(b.id));
    const filteredIds = new Set(filteredQuestions.map(q => q.id));

    const oldIdx = filteredQuestions.findIndex(q => q.id === active.id);
    const newIdx = filteredQuestions.findIndex(q => q.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;

    const newFilteredOrder = arrayMove([...filteredQuestions], oldIdx, newIdx);

    // Slot the reordered filtered items back into the full sorted list
    let filteredPtr = 0;
    const newSorted = sorted.map(q =>
      filteredIds.has(q.id) ? newFilteredOrder[filteredPtr++] : q
    );

    // Optimistic update
    setQuestions(newSorted.map((q, i) => ({ ...q, order_index: i })));

    // Persist
    try {
      // Only order_index changes here — resending question/answer text could
      // interact with server-side versioning of tracked questions.
      await Promise.all(newSorted.map((q, i) =>
        tenantApi.updatePrimerQuestion(q.id, { order_index: i })
      ));
    } catch (err) {
      console.error('Failed to reorder questions:', err);
      await fetchQuestions(); // rollback on error
    }
  };

  // ── Download helpers ──────────────────────────────────────────────────

  function stripHtml(html: string): string {
    return html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<\/li>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  function getStatusSuffix(): string {
    if (selectedStatus === 'active') return 'active';
    if (selectedStatus === 'inactive') return 'inactive';
    return 'all';
  }

  function getTabDownloadMeta(): { questions: typeof filteredQuestions; title: string; filePrefix: string } {
    const company = (tenant?.name ?? 'Company').toUpperCase();
    switch (currentView) {
      case 'faq':
        return { questions: filteredFaqQuestions, title: `${company} - FREQUENTLY ASKED QUESTIONS`, filePrefix: 'faq_questions' };
      case 'root':
        return { questions: filteredConversionQuestions, title: `${company} - CONVERSION AGENT QUESTIONS`, filePrefix: 'conversion_questions' };
      case 'other':
        return { questions: filteredOtherQuestions, title: `${company} - UNASSIGNED QUESTIONS`, filePrefix: 'unassigned_questions' };
      default:
        return { questions: filteredQuestions, title: `${company} - ALL QUESTIONS`, filePrefix: 'all_questions' };
    }
  }

  function downloadQuestionsAsTxt() {
    const { questions, title, filePrefix } = getTabDownloadMeta();
    const separator = '='.repeat(title.length);
    const lines: string[] = [title, separator, ''];
    for (const q of questions) {
      lines.push(`Q: ${q.question}`);
      lines.push(`A: ${stripHtml(q.answer)}`);
      lines.push(`Status: ${q.is_active ? 'Active' : 'Inactive'}`);
      lines.push('');
      lines.push('---');
      lines.push('');
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filePrefix}_${getStatusSuffix()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadQuestionsAsCsv() {
    const escape = (s: string) => `"${s.replace(/"/g, '""')}"`;
    const { questions, title, filePrefix } = getTabDownloadMeta();
    const separator = '='.repeat(title.length);
    const rows: string[] = [title, separator, 'Question,Answer,Status'];
    for (const q of questions) {
      rows.push([escape(q.question), escape(stripHtml(q.answer)), escape(q.is_active ? 'Active' : 'Inactive')].join(','));
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filePrefix}_${getStatusSuffix()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── SDL / LAWMA Labs helpers ───────────────────────────────────────────

  const brandForSDL = resolveSDLBrandName(tenant, tenant?.subdomain);

  const handleDownloadSDL = () => {
    downloadSDLPayloadAsJson(buildOptimizableSDLPayload(questions, brandForSDL, config), 'sdl_data.json');
  };

  const handleDownloadAllSDL = () => {
    downloadSDLPayloadAsJson(buildAllQuestionsSDLPayload(questions, brandForSDL, config), 'sdl_data_all.json');
  };

  const handleDownloadExternallyDistributableSDL = () => {
    downloadSDLPayloadAsJson(buildAllQuestionsSDLPayload(questions, brandForSDL, config), DISTRIBUTABLE_SDL_FILENAME);
  };

  // ── Shared props for QuestionItem ──────────────────────────────────────

  const questionItemProps = {
    allQuestions: questions,
    expandedCards,
    setExpandedCards,
    displayFollowUps,
    setDisplayFollowUps,
    questionCTAs,
    getFollowUpQuestions,
    handleManageCTAs,
    handleAddFollowUp,
    handleEdit: (q: PrimerQuestion) => handleEdit(q),
    handleToggleActive,
    handleDelete,
    handleRemoveCTA,
    setShowTreeView,
    availableLabels,
    handleCreateLabel,
    handleAssignLabels,
    getCTATypeColor,
    fetchQuestions,
    handleViewPerformance: (q: PrimerQuestion) => setPerformanceQuestion(q),
    // No handleMoveUp/handleMoveDown — ordering is via drag-and-drop on the All list
  };

  // ── Render ─────────────────────────────────────────────────────────────

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div>
      <QuestionAIAssistantDrawer
        isOpen={showAIOps}
        onClose={() => setShowAIOps(false)}
        questionIds={Array.from(selectedQuestions)}
        tabOrders={tabOrders}
        activeTab={activeTab}
        filters={aiFilters}
        onApplied={refreshAfterAIChanges}
      />
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Question Management</h2>
            <p className="text-sm text-gray-600 mt-1">Manage questions and their follow-ups.</p>
          </div>
          <div className="flex items-center flex-wrap gap-2">
            <button onClick={() => setShowAIOps(true)} className="bg-gradient-to-r from-indigo-500 to-violet-500 text-white px-4 py-2 hover:opacity-90 transition-opacity flex items-center space-x-2">
              <Sparkles className="h-4 w-4" /><span>Manage with AI</span>
            </button>
            <button onClick={() => setShowForm(true)} className="bg-indigo-600 text-white px-4 py-2  hover:bg-indigo-700 flex items-center space-x-2">
              <Plus className="h-4 w-4" /><span>Create Question</span>
            </button>
            <button onClick={() => setShowTreeView(true)} className="bg-gray-200 text-gray-700 px-4 py-2  hover:bg-gray-300 flex items-center space-x-2">
              <TreePine className="h-4 w-4" /><span>Question flow</span>
            </button>
            <button onClick={() => setShowSettings(true)} className="bg-gray-200 text-gray-700 p-2  hover:bg-gray-300" title="Settings">
              <Settings className="h-4 w-4" />
            </button>
          </div>
        </div>

        {cacheMessage && (
          <div className={`mt-4 p-3  border ${cacheMessageType === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
            <div className="flex items-center"><div className="text-sm">{cacheMessage}</div></div>
          </div>
        )}

        {/* View Navigation */}
        <div className="flex items-center flex-wrap gap-2 bg-white p-4 px-4 sm:px-6 shadow">
          {([
            { key: 'all' as const, icon: List, label: 'All', count: filteredQuestions.length },
            { key: 'root' as const, icon: TreePine, label: 'Conversion Agent', count: filteredConversionQuestions.length },
            { key: 'faq' as const, icon: List, label: 'FAQ Agent', count: filteredFaqQuestions.length },
            { key: 'other' as const, icon: Square, label: 'Unassigned', count: filteredOtherQuestions.length },
          ]).map(({ key, icon: Icon, label, count }) => (
            <button key={key} onClick={() => setCurrentView(key)}
              className={`${currentView === key ? 'bg-indigo-50 text-indigo-700  border border-indigo-200' : 'bg-white text-gray-600 hover:text-gray-800 hover:bg-gray-50'} px-4 py-2 font-medium text-sm flex items-center space-x-2 transition-colors`}>
              <Icon className="h-4 w-4" /><span>{label}</span>
              <span className={`${currentView === key ? 'bg-white text-indigo-800' : 'bg-gray-100 text-gray-700'} ml-1 py-0.5 px-2 rounded-full text-xs font-medium`}>{count}</span>
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200  p-4">
            <p className="text-sm text-red-800">{error}</p>
            <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800 ml-2">×</button>
          </div>
        )}

        {/* Tab Header & Filters */}
        <div className="bg-white shadow py-4 px-6 pb-6">
          <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {currentView === 'all' && 'All Questions'}
                {currentView === 'root' && 'Conversion Agent'}
                {currentView === 'faq' && 'FAQ Agent'}
                {currentView === 'other' && 'Unassigned Questions'}
              </h3>
              <p className="text-sm text-gray-700 mt-1">
                {currentView === 'all' && 'Manage all questions with bulk operations'}
                {currentView === 'root' && 'Questions assigned to the Conversion Agent'}
                {currentView === 'faq' && 'Questions displayed in the FAQ widget with static answers'}
                {currentView === 'other' && 'Questions not assigned to any agent (Conversion or FAQ). They can still be root questions or follow-ups.'}
              </p>
            </div>
            {/* Download dropdown */}
            <div className="relative flex-shrink-0" ref={tabDownloadDropdownRef}>
              <button
                onClick={() => setShowTabDownloadDropdown(prev => !prev)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center space-x-2 text-sm font-medium"
                title="Download questions"
              >
                <Download className="h-4 w-4" />
                <span>Download</span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-150 ${showTabDownloadDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showTabDownloadDropdown && (
                <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 shadow-lg z-20">
                  <button
                    onClick={() => { downloadQuestionsAsTxt(); setShowTabDownloadDropdown(false); }}
                    className="w-full flex items-center space-x-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700"
                  >
                    <Download className="h-4 w-4 flex-shrink-0" /><span>Download .txt</span>
                  </button>
                  <button
                    onClick={() => { downloadQuestionsAsCsv(); setShowTabDownloadDropdown(false); }}
                    className="w-full flex items-center space-x-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 border-t border-gray-100"
                  >
                    <Download className="h-4 w-4 flex-shrink-0" /><span>Download .csv</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="Search question" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm  bg-gray-50 hover:bg-gray-100" />
            </div>
            <div className="relative" ref={labelDropdownRef}>
              <button onClick={() => { setShowLabelDropdown(!showLabelDropdown); setPendingLabel(selectedLabel); }}
                className="px-4 py-3 w-full sm:w-40 text-sm text-gray-700  bg-gray-50 hover:bg-gray-100 flex justify-between items-center space-x-2">
                <span>{selectedLabel || 'Label'}</span><ChevronDown className="h-4 w-4" />
              </button>
              {showLabelDropdown && (
                <div className="absolute right-0 mt-2 w-52 bg-white border border-gray-200  shadow z-10">
                  <div className="p-4">
                    <div className="text-sm text-gray-800 font-medium mb-4">Filter by Label</div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input type="radio" name="labelFilter" checked={pendingLabel === '' || pendingLabel === 'all'} onChange={() => setPendingLabel('')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" />
                        <span className="text-sm text-gray-700">All</span>
                      </label>
                      {availableLabels.map(lbl => (
                        <label key={lbl.id} className="flex items-center space-x-2 cursor-pointer">
                          <input type="radio" name="labelFilter" checked={pendingLabel === lbl.name} onChange={() => setPendingLabel(lbl.name)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" />
                          <span className="text-sm text-gray-700">{lbl.name}</span>
                        </label>
                      ))}
                      {availableLabels.length === 0 && <p className="text-xs text-gray-400">No labels available.</p>}
                    </div>
                    <div className="mt-4 flex justify-end">
                      <button onClick={() => { setSelectedLabel(pendingLabel); setShowLabelDropdown(false); }} className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium  hover:bg-indigo-700">Apply</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="relative" ref={typeDropdownRef}>
              <button onClick={() => { setShowTypeDropdown(!showTypeDropdown); setPendingTypes(selectedTypes); }}
                className="px-4 py-3 w-full sm:w-40 text-sm text-gray-700  bg-gray-50 hover:bg-gray-100 flex justify-between items-center space-x-2">
                <span>Type{selectedTypes.length > 0 ? ` (${selectedTypes.length})` : ''}</span><ChevronDown className="h-4 w-4" />
              </button>
              {showTypeDropdown && (
                <div className="absolute right-0 mt-2 w-52 bg-white border border-gray-200  shadow z-10">
                  <div className="p-4">
                    <div className="text-sm text-gray-800 font-medium mb-4">Filter by Type</div>
                    <div className="space-y-2">
                      {[{ key: 'root', label: 'Root Question' }, { key: 'faq', label: 'FAQ Question' }, { key: 'conversion', label: 'Conversion Question' }, { key: 'first_class', label: 'First Class' }].map(({ key, label }) => (
                        <label key={key} className="flex items-center space-x-2 cursor-pointer">
                          <input type="checkbox" checked={pendingTypes.includes(key)} onChange={(e) => setPendingTypes(e.target.checked ? [...pendingTypes, key] : pendingTypes.filter(t => t !== key))}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 " />
                          <span className="text-sm text-gray-700">{label}</span>
                        </label>
                      ))}
                    </div>
                    <div className="mt-4 flex justify-end">
                      <button onClick={() => { setSelectedTypes(pendingTypes); setShowTypeDropdown(false); }} className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium  hover:bg-indigo-700">Apply</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="relative" ref={statusDropdownRef}>
              <button onClick={() => { setShowStatusDropdown(!showStatusDropdown); setPendingStatus(selectedStatus); setPendingLarsFilter(selectedLarsFilter); setPendingOptimizableFilter(selectedOptimizableFilter); }}
                className="px-4 py-3 w-full sm:w-40 text-sm text-gray-700  bg-gray-50 hover:bg-gray-100 flex justify-between items-center space-x-2">
                <span>{(() => { const parts: string[] = []; if (selectedStatus) parts.push(selectedStatus === 'active' ? 'Active' : 'Inactive'); if (selectedLarsFilter) parts.push('LARS'); if (selectedOptimizableFilter) parts.push('Optimizable'); return parts.length > 0 ? parts.join(', ') : 'Status'; })()}</span><ChevronDown className="h-4 w-4" />
              </button>
              {showStatusDropdown && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200  shadow z-10">
                  <div className="p-4">
                    <div className="text-sm text-gray-800 font-medium mb-3">Filter by Status</div>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input type="radio" name="statusFilter" checked={pendingStatus === ''} onChange={() => setPendingStatus('')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" />
                        <span className="text-sm text-gray-700">All</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input type="radio" name="statusFilter" checked={pendingStatus === 'active'} onChange={() => setPendingStatus('active')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" />
                        <span className="text-sm text-gray-700">Active</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input type="radio" name="statusFilter" checked={pendingStatus === 'inactive'} onChange={() => setPendingStatus('inactive')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" />
                        <span className="text-sm text-gray-700">Inactive</span>
                      </label>
                    </div>
                    <div className="border-t border-gray-200 mt-3 pt-3">
                      <div className="text-sm text-gray-800 font-medium mb-3">LARS Filters</div>
                      <div className="space-y-2">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input type="checkbox" checked={pendingLarsFilter} onChange={(e) => setPendingLarsFilter(e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" />
                          <span className="text-sm text-gray-700">LARS Imported</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input type="checkbox" checked={pendingOptimizableFilter} onChange={(e) => setPendingOptimizableFilter(e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" />
                          <span className="text-sm text-gray-700">Include In LARS</span>
                        </label>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <button onClick={() => { setSelectedStatus(pendingStatus); setSelectedLarsFilter(pendingLarsFilter); setSelectedOptimizableFilter(pendingOptimizableFilter); setShowStatusDropdown(false); }} className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium  hover:bg-indigo-700">Apply</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="relative" ref={contextTypeDropdownRef}>
              <button onClick={() => { setShowContextTypeDropdown(!showContextTypeDropdown); setPendingContextType(selectedContextType); }}
                className="px-4 py-3 w-full sm:w-40 text-sm text-gray-700  bg-gray-50 hover:bg-gray-100 flex justify-between items-center space-x-2">
                <span>{selectedContextType || 'Context Type'}</span><ChevronDown className="h-4 w-4" />
              </button>
              {showContextTypeDropdown && (
                <div className="absolute right-0 mt-2 w-52 bg-white border border-gray-200  shadow z-10">
                  <div className="p-4">
                    <div className="text-sm text-gray-800 font-medium mb-4">Filter by Context Type</div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input type="radio" name="contextTypeFilter" checked={pendingContextType === ''} onChange={() => setPendingContextType('')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" />
                        <span className="text-sm text-gray-700">All</span>
                      </label>
                      {availableContextTypes.map((contextType) => (
                        <label key={contextType} className="flex items-center space-x-2 cursor-pointer">
                          <input type="radio" name="contextTypeFilter" checked={pendingContextType === contextType} onChange={() => setPendingContextType(contextType)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" />
                          <span className="text-sm text-gray-700">{contextType}</span>
                        </label>
                      ))}
                      {availableContextTypes.length === 0 && <p className="text-xs text-gray-400">No context types available.</p>}
                    </div>
                    <div className="mt-4 flex justify-end">
                      <button onClick={() => { setSelectedContextType(pendingContextType); setShowContextTypeDropdown(false); }} className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium  hover:bg-indigo-700">Apply</button>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* All Questions View */}
        {currentView === 'all' && (
          <div className="bg-white shadow">
            <div className="px-6 pt-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <input type="checkbox" checked={selectedQuestions.size === filteredQuestions.length && filteredQuestions.length > 0} onChange={handleSelectAll} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 " />
                  <span className="text-sm text-gray-600">{selectedQuestions.size > 0 ? `${selectedQuestions.size} of ${filteredQuestions.length} selected` : `${filteredQuestions.length} question${filteredQuestions.length !== 1 ? 's' : ''}`}</span>
                </div>
                {selectedQuestions.size > 0 && (
                  <div className="relative" ref={bulkAssignDropdownRef}>
                    <button onClick={() => setShowBulkAssignDropdown(prev => !prev)} className="bg-indigo-600 text-white px-3 py-1.5 hover:bg-indigo-700 flex items-center space-x-2 text-sm">
                      <Settings className="h-4 w-4" /><span>Actions</span>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-150 ${showBulkAssignDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    {showBulkAssignDropdown && (
                      <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 shadow-lg z-20">
                        <button onClick={() => { handleBulkEnableLars(); setShowBulkAssignDropdown(false); }} className="w-full flex items-center space-x-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700">
                          <Zap className="h-4 w-4 flex-shrink-0" /><span>Enable for LARS</span>
                        </button>
                        <button onClick={() => { handleBulkMakeFirstClass(); setShowBulkAssignDropdown(false); }} className="w-full flex items-center space-x-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 border-t border-gray-100">
                          <Star className="h-4 w-4 flex-shrink-0" /><span>Make First Class</span>
                        </button>
                        <button onClick={() => { handleBulkDelete(); setShowBulkAssignDropdown(false); }} className="w-full flex items-center space-x-2 px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 border-t border-gray-100">
                          <Trash2 className="h-4 w-4 flex-shrink-0" /><span>Delete Selected</span>
                        </button>
                        <button onClick={() => { setSelectedQuestions(new Set()); setShowBulkAssignDropdown(false); }} className="w-full flex items-center space-x-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 border-t border-gray-100">
                          <X className="h-4 w-4 flex-shrink-0" /><span>Clear Selection</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {filteredQuestions.length === 0 ? (
              <div className="p-8 text-center"><p className="text-gray-500">{searchTerm ? 'No questions match your search.' : 'No questions found.'}</p></div>
            ) : (
              <div className="p-6 space-y-4">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={filteredQuestions.map(q => q.id)} strategy={verticalListSortingStrategy}>
                    {filteredQuestions.map((question, index) => (
                      <SortableQuestionItem
                        key={question.id}
                        question={question}
                        level={0}
                        hierarchyPath={[]}
                        index={index}
                        checkboxSlot={
                          <input
                            type="checkbox"
                            checked={selectedQuestions.has(question.id)}
                            onChange={() => handleSelectQuestion(question.id)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                          />
                        }
                        {...questionItemProps}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            )}
          </div>
        )}

        {/* Conversion Agent */}
        {currentView === 'root' && (
          <div className="bg-white shadow">
            {filteredConversionQuestions.length === 0 ? (
              <div className="p-8 text-center"><p className="text-gray-500">{searchTerm ? 'No questions match your search.' : 'No conversion questions yet.'}</p></div>
            ) : (
              <div className="p-6 space-y-6">
                {filteredConversionQuestions.slice().sort((a, b) => a.order_index - b.order_index || a.id.localeCompare(b.id)).map((q, index, arr) => (
                  <div key={q.id} className="relative"><QuestionItem question={q} level={0} hierarchyPath={[]} index={index} totalSiblings={arr.length} {...questionItemProps} /></div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* FAQ Agent */}
        {currentView === 'faq' && (
          <div className="space-y-6">
            {faqQuestions.length > 0 && (
              <div className="bg-white shadow">
                <div className="px-6 py-6">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div><h3 className="text-lg font-semibold text-gray-900">Live FAQ Widget Preview</h3><p className="text-sm text-gray-600 mt-1">Preview how your FAQs will appear to users</p></div>
                    <button onClick={() => { if (!showFAQPreview) clearFAQLocalStorage(); setShowFAQPreview(!showFAQPreview); }} className="px-4 py-2 bg-indigo-600 text-white  hover:bg-indigo-700 flex items-center space-x-2">
                      {showFAQPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}<span>{showFAQPreview ? 'Hide' : 'Show'} Preview</span>
                    </button>
                  </div>
                </div>
                {showFAQPreview && <div className="p-6"><div key={faqKey} id="display-lawmafaqai" data-label={selectedLabel || ""} className="w-full max-w-[1200px] mx-auto" /></div>}
              </div>
            )}
            <div className="bg-white shadow">
              {filteredFaqQuestions.length === 0 ? (
                <div className="p-8 text-center"><p className="text-gray-500">{searchTerm ? 'No questions match your search.' : 'No FAQ questions yet.'}</p></div>
              ) : (
                <div className="p-6 space-y-6">
                  {filteredFaqQuestions.slice().sort((a, b) => a.order_index - b.order_index || a.id.localeCompare(b.id)).map((faq, index, arr) => (
                    <div key={faq.id} className="relative"><QuestionItem question={faq} level={0} hierarchyPath={[]} index={index} totalSiblings={arr.length} {...questionItemProps} /></div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Other / Unassigned */}
        {currentView === 'other' && (
          <div className="bg-white shadow">
            {filteredOtherQuestions.length === 0 ? (
              <div className="p-8 text-center"><p className="text-gray-500">{searchTerm ? 'No questions match your search.' : 'No unassigned questions yet.'}</p></div>
            ) : (
              <div className="p-6 space-y-6">
                {filteredOtherQuestions.map((question, index) => (
                  <div key={question.id} className="relative"><QuestionItem question={question} level={0} hierarchyPath={[]} index={index} totalSiblings={filteredOtherQuestions.length} {...questionItemProps} /></div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex ${editSource === 'tree-view' ? 'items-start pt-8 sm:pt-12' : 'items-center'} justify-center z-50 p-2 sm:p-4`}>
          <div className="bg-white p-4 sm:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">{editingQuestion ? 'Edit Primer Question' : 'Add Primer Question'}</h3>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
                <input type="text" value={formData.question} onChange={(e) => setFormData({ ...formData, question: e.target.value })} className="w-full px-3 py-2 border border-gray-300  focus:outline-none focus:ring-2 focus:ring-indigo-500" required placeholder="Enter the primer question..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Answer</label>
                <RichTextEditor
                  ref={editorRef}
                  value={formData.answer}
                  onChange={(val) => setFormData({ ...formData, answer: val })}
                  placeholder="Enter the preset answer..."
                  minHeight="150px"
                  enabledInserts={['stars', 'cta', 'carousel', 'asset']}
                  onInsertAsset={() => setShowAssetSelector(true)}
                />
                <p className="text-xs text-gray-500 mt-1">Use the toolbar to format text and insert custom components like CTAs, star ratings, and carousels.</p>
              </div>
              <div>
                <div className="flex items-center">
                  <input type="checkbox" id="is_conversion" checked={formData.is_conversion} onChange={(e) => { const is_conversion = e.target.checked; setFormData({ ...formData, is_conversion, is_root: is_conversion ? formData.is_root : false }); }} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 " />
                  <label htmlFor="is_conversion" className="ml-2 block text-sm text-gray-700">Include as Conversion</label>
                </div>
                {formData.is_conversion && (
                  <div className="mt-2 ml-6 pl-4 py-2 border-l border-gray-200 bg-gray-50">
                    <div className="flex items-center">
                      <input type="checkbox" id="is_root" checked={formData.is_root} onChange={(e) => setFormData({ ...formData, is_root: e.target.checked })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 " />
                      <label htmlFor="is_root" className="ml-2 block text-sm text-gray-700">Root Question</label>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Root questions appear as popular questions in the main chat widget.</p>
                  </div>
                )}
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="is_faq" checked={formData.is_faq} onChange={(e) => setFormData({ ...formData, is_faq: e.target.checked, question_type: e.target.checked ? 'faq' : 'primer' })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 " />
                <label htmlFor="is_faq" className="ml-2 block text-sm text-gray-700">Include as FAQ</label>
              </div>
              <p className="text-xs text-gray-500">FAQ questions appear in the FAQ widget with accordion-style display</p>
              {formData.is_faq && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Answer Summary (Optional)</label>
                  <textarea value={formData.answer_summary || ''} onChange={(e) => setFormData({ ...formData, answer_summary: e.target.value || null })} rows={2} className="w-full px-3 py-2 border border-gray-300  focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Brief summary for FAQ preview (optional)..." />
                  <p className="text-xs text-gray-500 mt-1">Short summary shown in FAQ widget before users expand the full answer</p>
                </div>
              )}
              <div className="flex items-center">
                <input type="checkbox" id="is_optimizable" checked={formData.is_optimizable} onChange={(e) => setFormData({ ...formData, is_optimizable: e.target.checked })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 " />
                <label htmlFor="is_optimizable" className="ml-2 block text-sm text-gray-700">Include In LARS</label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="is_first_class" checked={formData.is_first_class} onChange={(e) => setFormData({ ...formData, is_first_class: e.target.checked })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 " />
                <label htmlFor="is_first_class" className="ml-2 block text-sm text-gray-700">First Class Question</label>
              </div>
              {formData.is_faq && formData.is_conversion && (
                <p className="text-xs text-gray-500">This question will appear in both the FAQ widget and conversion flow.</p>
              )}
              <div className="flex space-x-3 pt-4">
                <button type="submit" disabled={isSubmitting} className="flex-1 bg-indigo-600 text-white py-2 px-4 hover:bg-indigo-700 flex items-center justify-center space-x-2 disabled:opacity-70 disabled:cursor-not-allowed">
                  {isSubmitting ? (
                    <><span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" /><span>{editingQuestion ? 'Updating...' : 'Creating...'}</span></>
                  ) : (
                    <><Save className="h-4 w-4" /><span>{editingQuestion ? 'Update' : 'Create'}</span></>
                  )}
                </button>
                <button type="button" onClick={resetForm} className="flex-1 bg-gray-200 text-gray-800 py-2 px-4  hover:bg-gray-300">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modals */}
      <FollowUpSelector isOpen={showFollowUpSelector} onClose={() => { setShowFollowUpSelector(false); setSelectedParentForFollowUp(null); }} onSelect={handleFollowUpSelection} availableQuestions={questions} currentFollowUps={selectedParentForFollowUp ? getFollowUpQuestions(selectedParentForFollowUp.id).map(q => q.id) : []} parentQuestion={selectedParentForFollowUp || questions[0]} />
      <CTASelector isOpen={showCTASelector} onClose={() => { setShowCTASelector(false); setSelectedQuestionForCTA(null); }} onSelect={handleCTASelection} availableCTAs={ctas} currentCTAs={selectedQuestionForCTA ? (questionCTAs[selectedQuestionForCTA.id] || []).map(cta => cta.id) : []} question={selectedQuestionForCTA || questions[0]} />
      <ParentRemovalSelector isOpen={showParentRemovalSelector} onClose={() => { setShowParentRemovalSelector(false); setSelectedQuestionForParentRemoval(null); }} onRemove={handleParentRemoval} question={selectedQuestionForParentRemoval || questions[0]} allQuestions={questions} />
      <TreeViewModal isOpen={showTreeView} onClose={() => setShowTreeView(false)} questions={questions} getFollowUpQuestions={getFollowUpQuestions} questionCTAs={questionCTAs} onEditQuestion={(question) => { setShowTreeView(false); handleEdit(question, 'tree-view'); }} />
      <AssetSelectorModal isOpen={showAssetSelector} onClose={() => setShowAssetSelector(false)} onAssetsSelected={handleAssetsSelected} />
      <QuestionPerformanceModal question={performanceQuestion} isOpen={performanceQuestion !== null} onClose={() => setPerformanceQuestion(null)} />

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white  p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div><h3 className="text-lg font-semibold text-gray-900">Primer Questions Settings</h3><p className="text-sm text-gray-600 mt-1">Configure how primer questions behave in your chat widget</p></div>
              <button onClick={() => setShowSettings(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-6">
              <div className="bg-indigo-50 border border-indigo-200  p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0"><div className="w-10 h-10 bg-indigo-100  flex items-center justify-center"><Settings className="h-5 w-5 text-indigo-600" /></div></div>
                  <div className="flex-1">
                    <h4 className="text-lg font-medium text-indigo-900 mb-3">Follow-up Behavior Settings</h4>
                    <div className="flex items-start space-x-4 mb-4">
                      <label className="relative inline-flex items-center cursor-pointer mt-1">
                        <input type="checkbox" checked={config?.revert_to_root_on_deadend || false} onChange={(e) => handleConfigChange(e.target.checked)} disabled={configLoading} className="sr-only peer" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-indigo-800">Show root questions when follow-ups are exhausted</div>
                        <div className="text-xs text-indigo-600 mt-1">When enabled, root primer questions will be suggested as follow-ups when a conversation reaches the end of a primer question chain.</div>
                      </div>
                      {configLoading && <div className="flex-shrink-0 mt-1"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div></div>}
                    </div>
                    {configError && <div className="mb-4 bg-red-50 border border-red-200  p-3"><p className="text-sm text-red-800">{configError}</p></div>}
                    <div className="p-3 bg-indigo-100 ">
                      <h5 className="text-sm font-medium text-indigo-900 mb-2">How this works:</h5>
                      <ul className="text-xs text-indigo-800 space-y-1">
                        <li>• When <strong>enabled</strong>: After a user exhausts all follow-up questions in a chain, they'll see root questions as suggested follow-ups</li>
                        <li>• When <strong>disabled</strong>: Conversations will end naturally when there are no more follow-up questions available</li>
                        <li>• This setting applies to all primer question conversations for your account</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* LAWMA Labs Section */}
              <div className="bg-purple-50 border border-purple-200  p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0"><div className="w-10 h-10 bg-purple-100  flex items-center justify-center"><Send className="h-5 w-5 text-purple-600" /></div></div>
                  <div className="flex-1">
                    <h4 className="text-lg font-medium text-purple-900 mb-1">LAWMA Labs</h4>
                    <p className="text-xs text-purple-600 mb-4">Actions apply only to questions marked as <strong>Include In LARS</strong>. <strong>Download All</strong> includes every question.</p>
                    <div className="flex flex-wrap gap-3">
                      {/* Split button: Download SDL (optimizable) + dropdown for Download All */}
                      <div className="relative flex" ref={downloadDropdownRef}>
                        <button onClick={handleDownloadSDL} className="flex items-center space-x-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 text-sm font-medium hover:bg-purple-50 transition-colors border-r-0">
                          <Download className="h-4 w-4" /><span>Download SDL</span>
                        </button>
                        <button
                          onClick={() => setShowDownloadDropdown(prev => !prev)}
                          className="flex items-center px-2 py-2 bg-white border border-purple-300 text-purple-700 hover:bg-purple-50 transition-colors border-l border-l-purple-200"
                          title="More download options"
                        >
                          <ChevronDown className={`h-4 w-4 transition-transform duration-150 ${showDownloadDropdown ? 'rotate-180' : ''}`} />
                        </button>
                        {showDownloadDropdown && (
                          <div className="absolute left-0 top-full mt-1 w-52 bg-white border border-gray-200 shadow-lg z-20">
                            <button
                              onClick={() => { handleDownloadExternallyDistributableSDL(); setShowDownloadDropdown(false); }}
                              className="w-full flex items-center space-x-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700"
                              title="Same data as Download All; use this file at yourdomain.com/sdl.json"
                            >
                              <Download className="h-4 w-4 flex-shrink-0 font-bold" /><span>Download LARS Enabled Questions</span>
                            </button>
                            <button
                              onClick={() => { handleDownloadAllSDL(); setShowDownloadDropdown(false); }}
                              className="w-full flex items-center space-x-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 border-t border-gray-100"
                            >
                              <Download className="h-4 w-4 flex-shrink-0" /><span>Download All (JSON)</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end"><button onClick={() => setShowSettings(false)} className="px-4 py-2 bg-indigo-600 text-white  hover:bg-indigo-700">Close</button></div>
          </div>
        </div>
      )}

      {/* Bulk Delete Warning */}
      {showBulkDeleteWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white  p-6 w-full max-w-4xl">
            <div className="flex items-start space-x-4">
              <AlertTriangle className="h-8 w-8 text-yellow-500 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Cannot Delete Questions with Parent Relationships</h3>
                <p className="text-sm text-gray-700 mb-4">Some selected questions cannot be deleted because they have parent relationships.</p>
                {questionsWithParents.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Questions with parents ({questionsWithParents.length}):</h4>
                    <div className="max-h-40 overflow-y-auto bg-gray-50  p-3">
                      {questionsWithParents.map(q => (<div key={q.id} className="text-sm text-gray-700 py-1 border-b border-gray-200 last:border-b-0"><span className="font-medium">"{q.question}"</span><span className="text-xs text-gray-500 ml-2">({q.parent_ids?.length || 0} parent{(q.parent_ids?.length || 0) !== 1 ? 's' : ''})</span></div>))}
                    </div>
                  </div>
                )}
                {questionsToDelete.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Questions that can be deleted ({questionsToDelete.length}):</h4>
                    <div className="max-h-40 overflow-y-auto bg-green-50  p-3">
                      {questionsToDelete.map(q => (<div key={q.id} className="text-sm text-gray-700 py-1 border-b border-gray-200 last:border-b-0"><span className="font-medium">"{q.question}"</span><span className="text-xs text-green-600 ml-2">(Orphaned)</span></div>))}
                    </div>
                  </div>
                )}
                <div className="flex justify-end space-x-3">
                  <button onClick={handleCancelBulkDelete} className="px-4 py-2 bg-gray-200 text-gray-800  hover:bg-gray-300">Cancel</button>
                  {questionsToDelete.length > 0 && <button onClick={() => { setShowBulkDeleteWarning(false); setShowBulkDeleteConfirm(true); }} className="px-4 py-2 bg-green-600 text-white  hover:bg-green-700">Delete {questionsToDelete.length} Orphaned Question{questionsToDelete.length !== 1 ? 's' : ''}</button>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirm */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white  p-6 w-full max-w-4xl">
            <div className="flex items-start space-x-4">
              <AlertTriangle className="h-8 w-8 text-red-500 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Bulk Deletion</h3>
                <p className="text-sm text-gray-700 mb-4">Are you sure you want to permanently delete {questionsToDelete.length} question{questionsToDelete.length !== 1 ? 's' : ''}?</p>
                <div className="max-h-60 overflow-y-auto bg-red-50  p-4 border border-red-200 mb-4">
                  {questionsToDelete.map(q => (
                    <div key={q.id} className="mb-3 last:mb-0">
                      <div className="text-sm font-medium text-gray-900">"{q.question}"</div>
                      <div className="text-xs text-gray-600 mt-1 line-clamp-2">{q.answer.substring(0, 150)}...</div>
                      <div className="flex items-center space-x-2 mt-2">
                        {q.is_root && <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Root</span>}
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Orphaned</span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${q.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{q.is_active ? 'Active' : 'Inactive'}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-red-600 font-medium">This action cannot be undone.</p>
                <div className="mt-4 flex justify-end space-x-3">
                  <button onClick={handleCancelBulkDelete} className="px-4 py-2 bg-gray-200 text-gray-800  hover:bg-gray-300">Cancel</button>
                  <button onClick={handleConfirmBulkDelete} className="px-4 py-2 bg-red-600 text-white  hover:bg-red-700">Delete {questionsToDelete.length} Question{questionsToDelete.length !== 1 ? 's' : ''}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
